import { useMutation } from '@tanstack/react-query';
import { ChoixLangue } from '../i18n/ChoixLangue';
import { useTraduction } from '../i18n/langue';
import { useSession } from '../auth/session';
import { Carte, ChoixTheme } from '../ui';
import { ReglagesNotifications } from '../compte/ReglagesNotifications';
import { SourceConges } from '../compte/SourceConges';
import { versionLisible } from '../version';

/**
 * Réglages de l'application : langue et apparence.
 *
 * Les deux vivaient dans le bandeau, à côté de la marque, faute d'endroit où les mettre.
 * Ils s'y disputaient la largeur avec le badge de formule et l'adresse du compte, et
 * chaque écran de l'application portait en permanence deux réglages qu'on touche trois
 * fois dans sa vie.
 *
 * **Ils restent dans l'en-tête tant qu'on n'est pas connecté**, et c'est délibéré : cet
 * écran-ci exige une session, alors que quelqu'un qui ouvre l'application la nuit doit
 * pouvoir l'assombrir *avant* de saisir son mot de passe, pas après — et un parent invité
 * qui arrive dans la mauvaise langue doit pouvoir en changer sur l'écran de connexion.
 *
 * Chaque réglage porte une phrase d'aide. « Automatique » ne dit pas de lui-même qu'il
 * suit le téléphone, et c'est pourtant le réglage que la plupart des gens veulent.
 */
export function Options() {
  const { locale, messages } = useTraduction();
  const { client, profil, rafraichirProfil } = useSession();
  const m = messages.compte;

  /**
   * L'accord donné à la lecture des justificatifs, et surtout son **retrait**.
   *
   * Il se donne au premier justificatif déposé, dans le contexte où il a un sens. Il se
   * reprend ici, sans condition et sans rien à défaire — aucune image n'a été conservée.
   * Un consentement qu'on ne peut pas reprendre n'en est pas un, et l'endroit où on le
   * reprend doit être trouvable sans chercher.
   */
  const accord = useMutation({
    mutationFn: (valeur: boolean) =>
      client.request('/auth/receipt-scan-consent', { method: 'PATCH', body: { consent: valeur } }),
    onSuccess: () => rafraichirProfil(),
  });

  const actif = profil?.receiptScanConsent === true;

  /**
   * Le retour à l'écran d'où l'on vient.
   *
   * L'historique du navigateur, et non une adresse fixe : « Options » s'ouvre depuis
   * n'importe quel écran par le menu du compte, et renvoyer tout le monde à l'accueil
   * ferait perdre son dossier à qui y était. Ouvert directement par son adresse — un
   * signet, un onglet neuf — il n'y a pas de page précédente : l'accueil est alors la
   * seule destination qui ait un sens, plutôt qu'un bouton sans effet.
   */
  const retour = (): void => {
    if (globalThis.history.length > 1) globalThis.history.back();
    else globalThis.location.assign(import.meta.env.BASE_URL);
  };

  return (
    <>
      {/* Discret par construction : c'est un chemin de sortie, pas une action de l'écran.
          Un `<button>` et non un lien — la destination n'est pas une adresse, c'est
          « d'où je viens », et un lien mènerait ailleurs au clic du milieu. Le chevron
          seul serait une cible de 12 px et une icône sans nom : le mot l'accompagne. */}
      <button type="button" className="retour-page" onClick={retour}>
        <span aria-hidden="true">‹</span> {m.retourPrecedent}
      </button>

      {/* Le titre s'écrit désormais dans le bandeau, à la place du mot-symbole
          (`Coquille.tsx`, `TITRES_STATIQUES`). Il reste ici comme `<h1>` invisible : le
          plan du document ne doit pas venir d'un bandeau qui vit hors du `<main>` et se
          répète d'un écran à l'autre. Le dessin change, le plan ne change pas. */}
      <h1 className="invisible">{m.optionsTitre}</h1>

      <Carte titre={m.apparence}>
        <ChoixTheme />
        <p className="discret" style={{ marginBottom: 0 }}>
          {m.apparenceAide}
        </p>
      </Carte>

      <Carte titre={m.langueTitre}>
        <ChoixLangue />
        <p className="discret" style={{ marginBottom: 0 }}>
          {m.langueAide}
        </p>
      </Carte>

      {/* Les heures silencieuses, au-dessus des congés : c'est le réglage de cet écran
          qu'on vient chercher pour une raison précise — le téléphone est resté muet hier
          soir — alors que les autres se touchent une fois et s'oublient. */}
      <ReglagesNotifications />

      {/* Les congés scolaires sont un réglage **du dossier**, posé depuis un écran de
          compte : c'est la seule carte de cet écran dont l'effet est visible par l'autre
          parent, et elle le dit elle-même. La poser ailleurs aurait voulu dire un réglage
          de plus dans le dossier, pour une chose qu'on touche une fois par an. */}
      <SourceConges />

      <Carte titre={m.lectureIA}>
        <p style={{ marginTop: 0 }}>{m.lectureIAAide}</p>
        {/* Deux boutons plutôt qu'une bascule : ils nomment ce qu'ils font — « lire mes
            justificatifs », « ne rien envoyer » — là où une bascule laisse deviner quel
            état signifie quoi. `aria-pressed` dit lequel est en vigueur. */}
        <div className="choix-theme" role="group" aria-label={m.lectureIA}>
          <button
            type="button"
            className={actif ? 'choix-theme-option actif' : 'choix-theme-option'}
            aria-pressed={actif}
            onClick={() => accord.mutate(true)}
            disabled={accord.isPending}
          >
            {m.lectureIAActive}
          </button>
          <button
            type="button"
            className={!actif ? 'choix-theme-option actif' : 'choix-theme-option'}
            aria-pressed={!actif}
            onClick={() => accord.mutate(false)}
            disabled={accord.isPending}
          >
            {m.lectureIAInactive}
          </button>
        </div>
      </Carte>

      {/* La signature et la version, reprises du pied de page — mot pour mot, et depuis la
          même fonction. Le pied ne s'affiche plus sur téléphone, où il donnait à chaque
          écran une fin de site ; la version devait donc atterrir quelque part, et c'est
          ici qu'on vient déjà chercher ce qui n'est ni un dossier ni un geste. */}
      <p className="options-version discret">
        <a href="https://le-lab.net" target="_blank" rel="noreferrer noopener">
          le-lab.net
        </a>{' '}
        {messages.coquille.signature}
        <br />
        <time dateTime={__BUILD_TIME__}>{versionLisible(locale, messages)}</time>
      </p>
    </>
  );
}
